import {
  createRouter as createVueRouter,
  createWebHistory,
  isNavigationFailure,
  NavigationFailureType
} from 'vue-router';
import Layout from '/@/Layout.vue';
import { difference } from 'lodash';
import { router as authRoutes } from '/@/modules/auth/routes/auth.router';
import { router as devRoutes } from '/@/modules/dev/dev.router';
import { router as opsRoutes } from '/@/modules/ops/routes/ops.router';
import { router as exportRoutes } from '/@/modules/export/routes/export.router';
import { router as editorStandaloneRoutes } from '@/modules/export/_modules/editor/routes/editor.standalone.router';
import { router as checklistRoutes } from '/@/modules/guidancestep/routes/guidancestepchecklist.router';
import { router as probateTaxRoutes } from '/@/modules/tax/routes/calculator.probatetax.router';
import { router as referralUnprotectedRoutes } from './modules/referral/routes/referral.external.router';
import { router as bookingLinkUnprotectedRoutes } from './modules/bookinglink/routes/BookingLinkExternal.router';
import { router as esignRoutes } from '/@/modules/esign/routes/esign.router';
import { router as checkoutRoutes } from '/@/modules/billing/routes/checkout.router';
import { router as reviewRoutes } from '/@/modules/trustpilot/routes/review.router';
import { router as referralRoutes } from '/@/modules/platformreferral/routes/platformreferral.router';
import { router as sharedDocumentRoutes } from '/@/modules/document/routes/document.unauthenticated.router';
import { router as purchaseSummaryRoutes } from '/@/modules/billing/routes/purchase-summary.router';
import { router as toolsRoutes } from '/@/modules/typeform/routes/tools.router';
import { router as deprecatedRoutes } from '/@/modules/auth/routes/deprecated.router';
import { router as teamExternalRoutes } from '/@/modules/team/routes/team.external.router';
import AuthService from '/@/modules/auth/services/AuthService';
import { commits, getters } from '/@/modules/core/app/helpers/store';
import AnalyticsService from '@/integrations/analytics/AnalyticsService';
import { ModalBrowserBackButtonEvent } from '/@/modules/core/ui/ui.constants';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import ModalService from '/@/modules/core/ui/services/ModalService';
import { fromSnakeCaseToPascalCase } from '/@/modules/core/app/helpers/StringHelper';
import { AuthRoute, AuthUserRoute } from '/@/modules/auth/routes/auth.routes';
import ModelRelationshipProxy from '/@/modules/core/app/models/relationships/ModelRelationshipProxy';
import HubSpotScriptService from '/@/integrations/hubspot/HubSpotScriptService';
import Lang from '/@/modules/core/app/services/Lang';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import Locale from '/@/modules/user/Enum/Locale';
import { UnTrackablePages } from '/@/enums/UnTrackablePages';
import NavigatorHelper from '@/modules/core/app/helpers/NavigatorHelper';
import DocumentUIService from '/@/modules/core/app/services/DocumentUIService';
import LocalStorage from '@/modules/core/app/helpers/LocalStorage';
import { LocalStorageKey } from '@/modules/core/app/constants/app.constants';
import { LocaleQueryParam } from '@/modules/user/constants/LocaleQueryParam';
import { Env } from '@/Env';

let routes = [];
const routers = import.meta.glob(
  ['./modules/**/*.router.js', './modules/**/*.router.ts', '!./modules/**/ops*.router.js'],
  {
    eager: true
  }
);
for (const path in routers) {
  const moduleConfig = routers[path];
  const currentRoutes = (moduleConfig.default || moduleConfig).router;
  routes = routes.concat(currentRoutes);
}

export const createRouter = () => {
  const router = createVueRouter({
    history: createWebHistory(Env.nodeEnv().BASE_URL),
    routes: [
      {
        path: '/',
        component: Layout,
        children: difference(routes, [
          ...authRoutes,
          ...devRoutes,
          ...exportRoutes,
          ...editorStandaloneRoutes,
          ...checklistRoutes,
          ...sharedDocumentRoutes,
          ...probateTaxRoutes,
          ...referralUnprotectedRoutes,
          ...bookingLinkUnprotectedRoutes,
          ...esignRoutes,
          ...checkoutRoutes,
          ...deprecatedRoutes,
          ...reviewRoutes,
          ...referralRoutes,
          ...purchaseSummaryRoutes,
          ...teamExternalRoutes,
          ...toolsRoutes
        ]),
        meta: { authOnly: true }
      },
      ...authRoutes,
      ...opsRoutes,
      ...devRoutes,
      ...exportRoutes,
      ...editorStandaloneRoutes,
      ...checklistRoutes,
      ...sharedDocumentRoutes,
      ...probateTaxRoutes,
      ...referralUnprotectedRoutes,
      ...bookingLinkUnprotectedRoutes,
      ...esignRoutes,
      ...checkoutRoutes,
      ...deprecatedRoutes,
      ...reviewRoutes,
      ...referralRoutes,
      ...purchaseSummaryRoutes,
      ...teamExternalRoutes,
      ...toolsRoutes
    ],
    scrollBehavior: (to, from, savedPosition) => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ el: to.hash, behavior: 'smooth' });
        }, 500);
      });
    }
  });

  router.previousRoute = null;

  const beforeEachHook = async (to, from) => {
    if (!ModelRelationshipProxy.proxy) {
      await ModelRelationshipProxy.init();
    }

    if (canPopModal()) {
      return false;
    }

    if (to.matched.some((record) => record.meta.authOnly)) {
      const authUser = await AuthService.getAuthUser();

      if (!authUser) {
        return {
          name: 'login',
          query: { redirect: to.fullPath }
        };
      }

      await setLanguage(to, authUser);

      if (redirectToEmailVerify(authUser, to)) {
        return { name: AuthUserRoute.VERIFY_EMAIL.name };
      } else if (redirectToOtpSetup(authUser, to)) {
        return { name: AuthUserRoute.OTP_SETUP.name };
      } else if (redirectToTriage(authUser, to)) {
        return { name: AuthUserRoute.ONBOARDING_FLOW.name };
      } else if (to.path === '/') {
        return { name: authUser.homePage };
      }
    } else {
      await setLanguage(to);

      if (to.name !== AuthRoute.LOGIN.name) {
        commits.auth.setUser(null);
      }
    }
  };

  const afterEachHook = (to, from) => {
    const base = window.location.host;
    const referrer = window.referrer || `https://${base}${from.path}`;
    const path = `https://${base}${to.path}`;

    router.previousRoute = from;

    const theme = to.matched.find((record) => record.meta.theme)?.meta.theme;
    if (theme) {
      DocumentUIService.setTheme(theme);
    } else {
      DocumentUIService.clearTheme();
    }

    // we add a delay in order to track the page event on first browser load
    // segment seems to try and verify the user is actually on the browser url
    // we are wishing to capture.
    if (!UnTrackablePages.getByKey(to.name, 'name')) {
      setTimeout(() => {
        AnalyticsService.page(fromSnakeCaseToPascalCase(to.name), path, referrer, {
          search: window.location.search,
          title: document.title
        });
      }, 500);
    }

    // Since we are an SPA we need to force refresh the HubSpot chat
    HubSpotScriptService.track();
    HubSpotScriptService.refresh();
  };

  router.beforeEach(beforeEachHook);
  router.afterEach(afterEachHook);

  ['push', 'replace'].forEach((method) => {
    const originalMethod = router[method];

    router[method] = function (location) {
      return originalMethod.call(this, location).catch((error) => {
        if (
          !isNavigationFailure(error, NavigationFailureType.duplicated) &&
          !isNavigationFailure(error, NavigationFailureType.cancelled)
        ) {
          throw Error(error);
        }
      });
    };
  });

  return router;
};

function canPopModal() {
  const popStateDetected = getters.ui.getPopStateDetected();
  commits.ui.setPopStateDetected(false);
  if (popStateDetected && ModalService.isShowingModal) {
    EventBus.signal(ModalBrowserBackButtonEvent.ON_BACK_BUTTON_PRESSED);
    return true;
  }

  return false;
}

/**
 * @param {AuthUser} authUser
 * @param {Vue.Route} to
 * @returns {boolean}
 */
function redirectToTriage(authUser, to) {
  return (
    authUser.needsTriage &&
    to.name !== AuthUserRoute.ONBOARDING_FLOW.name &&
    to.name !== AuthUserRoute.ESTATE_SELECTION.name &&
    to.name !== AuthUserRoute.VERIFY_EMAIL.name &&
    to.name !== AuthUserRoute.OTP_SETUP.name
  );
}

/**
 * @param {AuthUser} authUser
 * @param {Vue.Route} to
 * @returns {boolean}
 */
function redirectToEmailVerify(authUser, to) {
  // Temporarily restricting this to only FAs
  return (
    !authUser.hasVerifiedEmail &&
    authUser.isInFinancialAdvisorProfessionGroup &&
    to.name !== AuthUserRoute.VERIFY_EMAIL.name
  );
}

/**
 * @param {AuthUser} authUser
 * @param {Vue.Route} to
 * @returns {boolean}
 */
function redirectToOtpSetup(authUser, to) {
  return (
    authUser.needsOtpSetup &&
    to.name !== AuthUserRoute.OTP_SETUP.name &&
    to.name !== AuthUserRoute.VERIFY_EMAIL.name
  );
}

async function setLanguage(route, authUser = null) {
  const locale = getLocale(route, authUser);
  const jurisdictionLanguages = authUser?.currentEstate?.jurisdiction.getLanguages() ?? [];
  const canToggleDeveloperMode = authUser?.permission?.canToggleDeveloperMode ?? false;

  await Lang.resetI18n(locale, jurisdictionLanguages, canToggleDeveloperMode);
  NumeralService.load(locale);
}

function getLocale(route, authUser) {
  let userLocale = authUser?.locale;
  let queryLocale = LocaleQueryParam.getLocale(route.query.lang);
  let localStorageLocale = LocalStorage.getItem(LocalStorageKey.USER_LOCALE);
  let navigatorLocale = NavigatorHelper.getUserLanguage();

  return userLocale || queryLocale || localStorageLocale || navigatorLocale || Locale.ENGLISH.key;
}

const router = createRouter();
export default router;
